import type {
	PutReportSettingsBody,
	ReportAthletesBySportQuery,
	ReportAthletesBySportRow,
	ReportCardRow,
	ReportCardsQuery,
	ReportHelpdeskTicketsQuery,
	ReportPersonRow,
	ReportRoomingQuery,
	ReportRoomingRow,
	ReportRows,
	ReportSettings,
	ReportTicketRow,
} from "@seagames/contracts";
import type { ReportAccreditationPeopleQuery } from "@seagames/contracts";
import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";

/**
 * Central Report Engine. Each report's rows are ONE request (`/reports/*`,
 * ≤ REPORT_ROWS_MAX, the module's own scope), fetched when the report is
 * previewed — never on page load. Setup is a cached read.
 */

const clean = (o: object) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "" && v !== null)) as Record<string, string>;

export const reportKeys = {
	all: ["reports"] as const,
	settings: ["reports", "settings"] as const,
};

export const reportSettingsOptions = () =>
	queryOptions({
		queryKey: reportKeys.settings,
		queryFn: ({ signal }) => api.get<ReportSettings>("/reports/settings", { signal }),
		staleTime: 5 * 60_000,
	});

export const putReportSettings = (body: PutReportSettingsBody) => api.put<ReportSettings>("/reports/settings", body);

export const fetchReportPeople = (q: ReportAccreditationPeopleQuery, signal?: AbortSignal) => api.get<ReportRows<ReportPersonRow>>("/reports/accreditation/people", { query: clean(q), signal });
export const fetchReportAthletesBySport = (q: ReportAthletesBySportQuery, signal?: AbortSignal) => api.get<ReportRows<ReportAthletesBySportRow>>("/reports/accreditation/athletes-by-sport", { query: clean(q), signal });
export const fetchReportCards = (q: ReportCardsQuery, signal?: AbortSignal) => api.get<ReportRows<ReportCardRow>>("/reports/cards", { query: clean(q), signal });
export const fetchReportTickets = (q: ReportHelpdeskTicketsQuery, signal?: AbortSignal) => api.get<ReportRows<ReportTicketRow>>("/reports/helpdesk/tickets", { query: clean(q), signal });
export const fetchReportRooming = (org: string, q: ReportRoomingQuery, signal?: AbortSignal) => api.get<ReportRows<ReportRoomingRow>>(`/reports/accommodation/rooming/${encodeURIComponent(org)}`, { query: clean(q), signal });
